import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Expander from "../../../../components/Expander";
import helpers from "../../../../common/utils/utils";
import AgeChart from "../../../../components/atoms/charts/AgeChart";
import Chart from "../../../../components/atoms/charts/Chart";
import {
  getGDCVersion,
  getGDCData,
} from "../../../../state/selectors/GDCComponent.selectors";
import {
  getVarsoUrl,
  userIsPremium,
  checkIfAllObjectValuesAreEmptyOrNull,
  removeKeysFromObject,
} from "../../../../common/helpers/helpers";
import PremiumEmptyStateChart from "../../../../components/atoms/charts/PremiumEmptyStateChart";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import ComponentPanel from "../../../../components/atoms/ComponentPanel/ComponentPanel";
import { titaniumWhite, whiteSmoke } from "../../../../common/helpers/colors";
import HelperText from "../../../../components/atoms/HelperText";

const borderColor = titaniumWhite;
const borderStyle = `1px solid ${borderColor}`;

const summaryTableHeadersTitles = [
  { title: "Tumor status", width: "15%" },
  { title: "Tumor tissue site", width: "13%" },
  { title: "Age at initial pathological diagnosis", width: "10%" },
  { title: "Race", width: "8%" },
  { title: "Ethnicity", width: "10%" },
  { title: "Histological type", width: "19%" },
  { title: "Vital status", width: "8%" },
  { title: "Sex", width: "10%" },
  { title: "History other malignacy", width: "10%" },
];

const summaryTableHeadersTitlesStats = [
  { title: "Total samples", width: "6%" },
  { title: "Tumor status", width: "12%" },
  { title: "Tumor tissue site", width: "21%" },
  { title: "Race", width: "15%" },
  { title: "Histological type", width: "21%" },
  { title: "Vital status", width: "8%" },
  { title: "Sex", width: "10%" },
  { title: "History other malignacy", width: "10%" },
];

class GDCComponent extends React.Component {
  constructor() {
    super();
    this.state = {};
  }

  getTreatments = (treatment, idx) => {
    const treatmentRows = [];
    let drugName;
    let prescribedDose;
    let totalDose;

    if (treatment.drug_name) {
      if (treatment.drug_name.length > 1) {
        drugName = treatment.drug_name.join(", ");
      } else {
        drugName = treatment.drug_name;
      }
    }

    if (treatment.prescribed_dose) {
      if (treatment.prescribed_dose.length > 1) {
        prescribedDose = treatment.prescribed_dose.join(", ");
      } else {
        prescribedDose = treatment.prescribed_dose;
      }
    }

    if (treatment.total_dose) {
      if (treatment.total_dose.length > 1) {
        totalDose = treatment.total_dose.join(", ");
      } else {
        totalDose = treatment.total_dose;
      }
    }

    const treatmentRowsText = (
      <tr key={`treatment_${idx}`} style={{ backgroundColor: whiteSmoke }}>
        <td className="varsome-text-center">
          {treatment.therapy_type ? treatment.therapy_type : "-"}
        </td>
        <td className="varsome-text-center">{drugName || "-"}</td>
        <td className="varsome-text-center">{prescribedDose || "-"}</td>
        <td className="varsome-text-center">{totalDose || "-"}</td>
        <td className="varsome-text-center">
          {treatment.measure_of_response ? treatment.measure_of_response : "-"}
        </td>
        <td className="varsome-text-center">
          {treatment.number_cycles ? treatment.number_cycles : "-"}
        </td>

        <td className="varsome-text-center">
          {treatment.therapy_on_going ? treatment.therapy_on_going : "-"}
        </td>
        <td className="varsome-text-center">
          {treatment.days_to_drug_therapy_start
            ? treatment.days_to_drug_therapy_start
            : "-"}
        </td>
        <td className="varsome-text-center">
          {treatment.days_to_drug_therapy_end
            ? treatment.days_to_drug_therapy_end
            : "-"}
        </td>
        <td className="varsome-text-center">
          {treatment.route_of_administration
            ? treatment.route_of_administration
            : "-"}
        </td>
      </tr>
    );

    treatmentRows.push(treatmentRowsText);

    return treatmentRows;
  };

  getItemRows = (item, i, itemRowsFilteredLength) => {
    const rows = [];

    const firstRowText = (
      <tr key={`first_row_${i}`}>
        <td className="varsome-text-center">
          {item.tumor_status
            ? helpers.capitalizeFirstLetter(item.tumor_status.toLowerCase())
            : "-"}
        </td>
        <td className="varsome-text-center">
          {item.tumor_tissue_site ? item.tumor_tissue_site : "-"}
        </td>
        <td className="varsome-text-center">
          {item.age_at_initial_pathologic_diagnosis
            ? item.age_at_initial_pathologic_diagnosis
            : "-"}
        </td>
        <td className="varsome-text-center">
          {item.race
            ? helpers.capitalizeFirstLetter(item.race.toLowerCase())
            : "-"}
        </td>
        <td className="varsome-text-center">
          {item.ethnicity
            ? helpers.capitalizeFirstLetter(item.ethnicity.toLowerCase())
            : "-"}
        </td>
        <td colSpan="2" className="varsome-text-center">
          {item.histological_type ? item.histological_type : "-"}
        </td>
        <td className="varsome-text-center">
          {item.vital_status ? item.vital_status : "-"}
        </td>
        <td className="varsome-text-center">
          {item.gender
            ? helpers.capitalizeFirstLetter(item.gender.toLowerCase())
            : "-"}
        </td>
        <td className="varsome-text-center">
          <a onClick={this.expandRow.bind(this, item)}>
            {item.expanded ? "Collapse" : "Expand"}
          </a>
        </td>
      </tr>
    );
    rows.push(firstRowText);

    if (item.expanded) {
      const caseUrl = `https://portal.gdc.cancer.gov/cases/${item.case_id}`;

      const projectUrl = `https://portal.gdc.cancer.gov/projects/${item.project_name}`;

      const detailsRowHeaders = (
        <tr
          key={`details_row_headers_${i}`}
          style={{ backgroundColor: whiteSmoke }}
        >
          <th style={{ textAlign: "center" }}>
            {"Pathologic T, N, M "}
            <HelperText fieldDescriptionKey="Pathologic T, N, M" />
          </th>
          <th style={{ textAlign: "center" }}>
            {"Clinical T, N, M "}
            <HelperText fieldDescriptionKey="Clinical T, N, M" />
          </th>
          <th className="tw-text-center">Pathologic state</th>
          <th className="tw-text-center">Year of diagnosis</th>
          <th className="tw-text-center">History other malignacy</th>
          <th colSpan="2" className="tw-text-center">
            Project name
          </th>
          <th colSpan="2" className="tw-text-center">
            Case id
          </th>
          <th className="tw-text-center">Days to death</th>
        </tr>
      );
      rows.push(detailsRowHeaders);

      const detailsRowText = (
        <tr
          key={`detais_row_text_${i}`}
          style={{ backgroundColor: whiteSmoke }}
        >
          <td className="varsome-text-center">
            {item.pathologic_t ? item.pathologic_t : "-"}
            {", "}
            {item.pathologic_n ? item.pathologic_n : "-"}
            {", "}
            {item.pathologic_m ? item.pathologic_m : "-"}
          </td>
          <td className="varsome-text-center">
            {item.clinical_t ? item.clinical_t : "-"}
            {", "}
            {item.clinical_n ? item.clinical_n : "-"}
            {", "}
            {item.clinical_m ? item.clinical_m : "-"}
          </td>
          <td className="varsome-text-center">
            {item.pathologic_state ? item.pathologic_state : "-"}
          </td>
          <td className="varsome-text-center">
            {item.year_of_initial_pathologic_diagnosis
              ? item.year_of_initial_pathologic_diagnosis
              : "-"}
          </td>
          <td className="varsome-text-center">
            {item.history_other_malignancy
              ? helpers.capitalizeFirstLetter(
                  item.history_other_malignancy.toLowerCase()
                )
              : "-"}
          </td>
          <td colSpan="2" className="varsome-text-center">
            <a href={getVarsoUrl(projectUrl)} target="_blank">
              {item.project_name}
            </a>
          </td>
          <td colSpan="2" className="varsome-text-center">
            <a href={getVarsoUrl(caseUrl)} target="_blank">
              view on GDC
            </a>
          </td>
          <td className="varsome-text-center">
            {item.days_to_death ? item.days_to_death : "-"}
          </td>
        </tr>
      );
      rows.push(detailsRowText);

      if (item.treatments) {
        const TreatmentsRowHeader = (
          <tr key={`treatments_header_${i}`}>
            <th
              className="tw-text-center"
              colSpan="10"
              style={{
                backgroundColor: whiteSmoke,
              }}
            >
              <h5>
                <b>Treatments</b>
              </h5>
            </th>
          </tr>
        );

        const treatmentRowsHeaders = (
          <tr
            key={`treatments_row_headers_${i}`}
            style={{ backgroundColor: whiteSmoke }}
          >
            <th className="tw-text-center">Therapy type</th>
            <th className="tw-text-center">Drug name</th>
            <th className="tw-text-center">Prescribed dose</th>
            <th className="tw-text-center">Total dose</th>
            <th className="tw-text-center">Measure of response</th>
            <th className="tw-text-center">Number cycles</th>
            <th className="tw-text-center">Therapy on going</th>
            <th className="tw-text-center">Days to drug therapy start</th>
            <th className="tw-text-center">Days to drug therapy end</th>
            <th className="tw-text-center">Route of administration</th>
          </tr>
        );

        rows.push(TreatmentsRowHeader);

        rows.push(treatmentRowsHeaders);

        const treatments = item.treatments.map((treatment, j) => {
          return this.getTreatments(treatment, j);
        });

        rows.push(treatments);
      }

      if (i !== itemRowsFilteredLength - 1) {
        const tableHeaders = this.getTableHeaders();
        rows.push(tableHeaders);
      }
    }
    return rows;
  };

  getTableHeaders = () => {
    return (
      <tr key="table_headers">
        <th width="15%" className="tw-text-center">
          Tumor status
        </th>
        <th width="13%" className="tw-text-center">
          Tumor tissue site
        </th>
        <th width="10%" className="tw-text-center">
          Age at initial pathological diagnosis
        </th>
        <th width="8%" className="tw-text-center">
          Race
        </th>
        <th width="10%" className="tw-text-center">
          Ethnicity
        </th>
        <th colSpan="2" width="19%" className="tw-text-center">
          Histological type
        </th>
        <th width="8%" className="tw-text-center">
          Vital status
        </th>
        <th width="8%" className="tw-text-center">
          Sex
        </th>
        <th width="10%" className="tw-text-center">
          Details
        </th>
      </tr>
    );
  };

  // eslint-disable-next-line consistent-return
  getItemsTable = (items) => {
    const itemRowsFiltered = items.reduce((result, item) => {
      for (const key in item) {
        if (
          key !== "variant_classification" &&
          key !== "consequence" &&
          key !== "case_id" &&
          key !== "project_name"
        ) {
          if (item[key] !== null && item[key] !== "") {
            result.push(item);
            break;
          }
        }
      }
      return result;
    }, []);

    let summmaryTable;
    if (itemRowsFiltered.length > 5) {
      summmaryTable = this.getSummaryTable(itemRowsFiltered);
    }

    const itemRowsFilteredLength = itemRowsFiltered.length;

    const itemRows = itemRowsFiltered.map((item, i) => {
      return this.getItemRows(item, i, itemRowsFilteredLength);
    });

    const tableHeaders = this.getTableHeaders();

    if (itemRows.length > 0) {
      return (
        <div>
          {summmaryTable}
          <table className="varsome-table">
            <thead>{tableHeaders}</thead>
            <Expander showMore="5" component="tbody" columns="10">
              {itemRows}
            </Expander>
          </table>
        </div>
      );
    }
  };

  countFrequency = (category) => {
    const result = category.reduce((obj, value) => {
      // eslint-disable-next-line no-plusplus
      obj[value] = ++obj[value] || 1;
      return obj;
    }, {});

    return result;
  };

  maxMinVal = (category) => {
    const obj = this.countFrequency(category);
    const sortedEntriesByVal = Object.entries(obj).sort(
      ([, v1], [, v2]) => v1 - v2
    );
    let total = 0;
    Object.entries(obj).forEach(([key, value]) => {
      if (key !== "null") {
        total += value;
      }
    });

    return {
      min: sortedEntriesByVal[0],
      max: sortedEntriesByVal[sortedEntriesByVal.length - 1],
      sortedObjByVal: sortedEntriesByVal.reduce(
        (r, [k, v]) => ({ ...r, [k]: v }),
        {}
      ),
      total,
    };
  };

  maxMinAvg = (arr) => {
    let max = arr[0];
    let min = arr[0];
    let sum = arr[0];

    let length = 0;
    if (arr[0] != null) {
      length = 1;
    }

    for (let i = 1; i < arr.length; i++) {
      if (arr[i] != null) {
        if (arr[i] > max) {
          max = arr[i];
        }
        if (arr[i] < min) {
          min = arr[i];
        }
        sum += arr[i];
        length += 1;
      }
    }

    return [max, min, sum / length, length];
  };

  getSummaryTableHeaders = (stats) => {
    let data = summaryTableHeadersTitles;
    let st = "";
    let style = { backgroundColor: whiteSmoke };
    if (stats) {
      data = summaryTableHeadersTitlesStats;
      st = "_stats";
      style = { backgroundColor: whiteSmoke, borderColor, border: borderStyle };
    }

    return (
      <tr key={`summary_table_headers${st}`} style={style}>
        {data.map((entry) => {
          return (
            <th
              className="tw-text-center"
              key={entry.title}
              width={entry.width}
            >
              {entry.title}
            </th>
          );
        })}
      </tr>
    );
  };

  getSummaryTableEntries = (
    age,
    tumorStatus,
    gender,
    vitalStatus,
    race,
    ethnicity,
    tumorTissueSite,
    histologicalType,
    historyOtherMalignacy
  ) => {
    return (
      <tr
        key="summary_table_row_entries"
        style={{ backgroundColor: whiteSmoke }}
      >
        <td className="varsome-text-center">
          <small>
            {"entries: "}
            {tumorStatus.total}
          </small>
        </td>
        <td className="varsome-text-center">
          <small>
            {"entries: "}
            {tumorTissueSite.total}
          </small>
        </td>
        <td className="varsome-text-center">
          <small>
            {"entries: "}
            {age[3]}
          </small>
        </td>
        <td className="varsome-text-center">
          <small>
            {"entries: "}
            {race.total}
          </small>
        </td>
        <td className="varsome-text-center">
          <small>
            {"entries: "}
            {ethnicity.total}
          </small>
        </td>
        <td className="varsome-text-center">
          <small>
            {"entries: "}
            {histologicalType.total}
          </small>
        </td>
        <td className="varsome-text-center">
          <small>
            {"entries: "}
            {vitalStatus.total}
          </small>
        </td>
        <td className="varsome-text-center">
          <small>
            {"entries: "}
            {gender.total}
          </small>
        </td>
        <td className="varsome-text-center">
          <small>
            {"entries: "}
            {historyOtherMalignacy.total}
          </small>
        </td>
      </tr>
    );
  };

  getSummaryTable = (filteredEntries) => {
    const result = {};

    filteredEntries.forEach((each) => {
      Object.keys(each).forEach((key) => {
        result[key] = result[key] || [];
        result[key].push(each[key]);
      });
    });

    const age = this.maxMinAvg(result.age_at_initial_pathologic_diagnosis);
    const tumorStatus = this.maxMinVal(result.tumor_status);
    const gender = this.maxMinVal(result.gender);
    const vitalStatus = this.maxMinVal(result.vital_status);
    const race = this.maxMinVal(result.race);
    const ethnicity = this.maxMinVal(result.ethnicity);
    const tumorTissueSite = this.maxMinVal(result.tumor_tissue_site);
    const histologicalType = this.maxMinVal(result.histological_type);
    const historyOtherMalignacy = this.maxMinVal(
      result.history_other_malignancy
    );

    const summaryHeaders = this.getSummaryTableHeaders();

    const summaryRow = (
      <tr key="summary_table_row" style={{ backgroundColor: whiteSmoke }}>
        <td className="varsome-text-center">
          {helpers.capitalizeFirstLetter(tumorStatus.max[0].toLowerCase())}
          {": "}
          {((tumorStatus.max[1] / tumorStatus.total) * 100).toFixed(1)}%
        </td>
        <td className="varsome-text-center">
          {helpers.capitalizeFirstLetter(tumorTissueSite.max[0].toLowerCase())}
          {": "}
          {((tumorTissueSite.max[1] / tumorTissueSite.total) * 100).toFixed(1)}%
        </td>
        <td className="varsome-text-center">
          {"Min: "}
          {age[1]}
          <br />
          {"Avg: "}
          {age[2].toFixed(0)}
        </td>
        <td className="varsome-text-center">
          {helpers.capitalizeFirstLetter(race.max[0].toLowerCase())}
          {": "}
          {((race.max[1] / race.total) * 100).toFixed(1)}%
        </td>
        <td className="varsome-text-center">
          {helpers.capitalizeFirstLetter(ethnicity.max[0].toLowerCase())}
          {": "}
          {((ethnicity.max[1] / ethnicity.total) * 100).toFixed(1)}%
        </td>
        <td className="varsome-text-center">
          {helpers.capitalizeFirstLetter(histologicalType.max[0].toLowerCase())}
          {": "}
          {((histologicalType.max[1] / histologicalType.total) * 100).toFixed(
            1
          )}
          %
        </td>
        <td className="varsome-text-center">
          {helpers.capitalizeFirstLetter(vitalStatus.max[0].toLowerCase())}
          {": "}
          {((vitalStatus.max[1] / vitalStatus.total) * 100).toFixed(1)}%
        </td>
        <td className="varsome-text-center">
          {helpers.capitalizeFirstLetter(gender.max[0].toLowerCase())}
          {": "}
          {((gender.max[1] / gender.total) * 100).toFixed(1)}%
        </td>
        <td className="varsome-text-center">
          {helpers.capitalizeFirstLetter(
            historyOtherMalignacy.max[0].toLowerCase()
          )}
          {": "}
          {(
            (historyOtherMalignacy.max[1] / historyOtherMalignacy.total) *
            100
          ).toFixed(1)}
          %
        </td>
      </tr>
    );

    const entries = this.getSummaryTableEntries(
      age,
      tumorStatus,
      gender,
      vitalStatus,
      race,
      ethnicity,
      tumorTissueSite,
      histologicalType,
      historyOtherMalignacy
    );

    return (
      <div>
        <h5> Summary of results </h5>
        <table className="varsome-table">
          <tbody>
            {summaryHeaders}
            {summaryRow}
            {entries}
          </tbody>
        </table>
        <br />
        <h5> Results </h5>
      </div>
    );
  };

  expandRow(row) {
    row.expanded = !row.expanded;
    this.forceUpdate();
  }

  render() {
    const { gdcVersion, gdcData } = this.props;
    let content = "";
    let rows;
    const gdcUrl = getVarsoUrl("https://gdc.cancer.gov/");
    const gdcAccessUrl = getVarsoUrl(
      "https://gdc.cancer.gov/about-gdc/gdc-policies"
    );
    const titleDetailsRight = (
      <div className="varsome-right">
        <small className="tw-mr-4">
          <a href={gdcAccessUrl} target="_blank">
            GDC access policy
          </a>
        </small>
      </div>
    );
    if (gdcData) {
      const value = gdcData.items;
      if (value) rows = this.getItemsTable(value);
      const {
        age_freq: ageFreq,
        ajcc_pathologic_tumor_stage: ajccPathologicTumorStage,
        cancer_name: cancerName,
        cancer_type: cancerType,
        clin_m_stage: clinMStage,
        clin_n_stage: clinNStage,
        clin_t_stage: clinTStage,
        clinical_significance: clinicalSignificance,
        drug,
        measure_of_response: measureOfResponse,
        oncotree_code: oncotreeCode,
        os_status: osStatus,
        path_m_stage: pathMStage,
        path_n_stage: pathNStage,
        path_t_stage: pathTStage,
        prior_dx: priorDx,
        race,
        sex,
        therapy_ongoing: therapyOngoing,
        therapy_type: therapyType,
        tissue_type: tissueType,
        tumor_tissue_site: tumorTissueSite,
        tumor_status: tumorStatus,
      } = gdcData;

      if (!rows && gdcData && gdcData.total_samples == null) {
        return null;
      }
      // rows are for old data structure and gdcData for new datastructure
      if (rows || (gdcData && gdcData.total_samples)) {
        const filteredData = removeKeysFromObject(gdcData, [
          "total_samples",
          "version",
          "study_name",
        ]);
        const isEmpty = checkIfAllObjectValuesAreEmptyOrNull(filteredData);
        content = (
          <div className="varsome-inner-container">
            {gdcData && gdcData.total_samples && (
              <h6>
                <b>{"Total samples: "}</b>
                {gdcData.total_samples.toLocaleString().replace(",", " ")}
              </h6>
            )}
            {!rows && isEmpty && (
              <div>
                No additional data available in VarSome, please see the full
                record on GDC.
              </div>
            )}
            <div className="tables-container charts-container">
              <AgeChart
                showEmptyState={false}
                data={ageFreq}
                title="Age frequency"
              />
              <Chart
                showEmptyState={false}
                data={ajccPathologicTumorStage}
                title="Ajcc pathologic tumor stage"
              />
              <Chart
                showEmptyState={false}
                data={cancerName}
                title="Cancer name"
              />
              <Chart
                showEmptyState={false}
                data={cancerType}
                title="Cancer type"
              />
              <Chart
                showEmptyState={false}
                data={clinMStage}
                title="Clinical m stage"
              />
              <Chart
                showEmptyState={false}
                data={clinNStage}
                title="Clinical n stage"
              />
              <Chart
                showEmptyState={false}
                data={clinTStage}
                title="Clinical t stage"
              />
              <Chart
                showEmptyState={false}
                data={clinicalSignificance}
                title="Clinical significance"
              />
              <Chart
                showEmptyState={false}
                data={measureOfResponse}
                title="Measure of response"
              />
              <Chart
                showEmptyState={false}
                data={oncotreeCode}
                title="Oncotree code"
              />
              <Chart
                showEmptyState={false}
                data={osStatus}
                title="Vital status"
              />
              <Chart
                showEmptyState={false}
                data={pathMStage}
                title="Pathological m stage"
              />
              <Chart
                showEmptyState={false}
                data={pathNStage}
                title="Pathological n stage"
              />
              <Chart
                showEmptyState={false}
                data={pathTStage}
                title="Pathological t stage"
              />
              <Chart
                data={priorDx}
                title="History other malignacy"
                showEmptyState={false}
              />
              <Chart data={race} title="Race" showEmptyState={false} />
              <Chart showEmptyState={false} data={sex} title="Sex" />
              <Chart
                showEmptyState={false}
                data={tissueType}
                title="Tissue type"
              />
              <Chart
                data={therapyOngoing}
                title="Therapy ongoing"
                showEmptyState={false}
              />
              <Chart
                data={therapyType}
                title="Therapy type"
                showEmptyState={false}
              />
              <Chart
                data={tumorStatus}
                title="Tumor status"
                showEmptyState={false}
              />
              <Chart
                data={tumorTissueSite}
                title="Tumor tissue site"
                showEmptyState={false}
              />
              {userIsPremium() || __IS_COMPONENTS_PORTAL__ ? (
                <Chart data={drug} title="Drug" showEmptyState={false} />
              ) : (
                <PremiumEmptyStateChart name="Drug" />
              )}
            </div>
            {rows}
          </div>
        );
      }
      return (
        <div className="varsome-result-fixed">
          <div className="varsome-container varsome-container__basic">
            <ComponentPanel
              version={gdcVersion}
              url={gdcUrl}
              titleDetailsRight={titleDetailsRight}
              content={content}
              title="NCI Genomic Data Commons"
            />
          </div>
        </div>
      );
    }

    return null;
  }
}

GDCComponent.propTypes = {
  gdcVersion: PropTypes.string,
  gdcData: PropTypes.shape({
    stats: PropTypes.shape({}),
    items: PropTypes.arrayOf(PropTypes.shape({})),
    age_freq: PropTypes.arrayOf(PropTypes.shape({})),
    oncotree_code: PropTypes.arrayOf(PropTypes.shape({})),
    cancer_type: PropTypes.arrayOf(PropTypes.shape({})),
    cancer_name: PropTypes.arrayOf(PropTypes.shape({})),
    tissue_type: PropTypes.arrayOf(PropTypes.shape({})),
    measure_of_response: PropTypes.arrayOf(PropTypes.shape({})),
    ajcc_pathologic_tumor_stage: PropTypes.arrayOf(PropTypes.shape({})),
    clin_m_stage: PropTypes.arrayOf(PropTypes.shape({})),
    clin_n_stage: PropTypes.arrayOf(PropTypes.shape({})),
    clin_t_stage: PropTypes.arrayOf(PropTypes.shape({})),
    clinical_significance: PropTypes.arrayOf(PropTypes.shape({})),
    path_m_stage: PropTypes.arrayOf(PropTypes.shape({})),
    path_n_stage: PropTypes.arrayOf(PropTypes.shape({})),
    path_t_stage: PropTypes.arrayOf(PropTypes.shape({})),
    race: PropTypes.arrayOf(PropTypes.shape({})),
    sex: PropTypes.arrayOf(PropTypes.shape({})),
    os_status: PropTypes.arrayOf(PropTypes.shape({})),
    prior_dx: PropTypes.arrayOf(PropTypes.shape({})),
    therapy_ongoing: PropTypes.arrayOf(PropTypes.shape({})),
    therapy_type: PropTypes.arrayOf(PropTypes.shape({})),
    tumor_tissue_site: PropTypes.arrayOf(PropTypes.shape({})),
    tumor_status: PropTypes.arrayOf(PropTypes.shape({})),
    total_samples: PropTypes.number,
    drug: PropTypes.string,
  }),
};

GDCComponent.defaultProps = {
  gdcVersion: null,
  gdcData: null,
};

const mapStateToProps = (state) => {
  return {
    gdcVersion: getGDCVersion(state),
    gdcData: getGDCData(state),
  };
};

export default connect(
  mapStateToProps,
  {}
)(withErrorGuard(GDCComponent, "GDCComponent", "NCI Genomic Data Commons"));
